/** Legacy template names still used in docs and older snippets. */
export const LEGACY_CUSTOM_CODE_COMPONENT_NAMES = [
	"MyComponent",
	"MyNextComponent",
	"DefaultComponent",
	"LandingPage",
	"CustomComponent",
] as const;

export function extractComponentName(source: string, fallback: string): string {
	const exportFuncMatch = source.match(/export\s+default\s+function\s+(\w+)/);
	if (exportFuncMatch) {
		return exportFuncMatch[1];
	}

	const exportRefMatch = source.match(/export\s+default\s+(\w+)\s*;?\s*$/m);
	if (exportRefMatch) {
		return exportRefMatch[1];
	}

	const namedMatch = source.match(/(?:const|function)\s+(\w+)\s*[=(]/);
	if (namedMatch) {
		return namedMatch[1];
	}

	return fallback;
}

/**
 * Names to try when mounting custom React code (iframe preview + public runtime).
 * Export name from source wins over the settings-panel "component name" field so
 * templates like `MyComponent` do not shadow `export default function Header`.
 */
export function resolveComponentNames(
	source: string,
	explicitName?: string
): string[] {
	const fromSource = extractComponentName(source, "");
	const explicit = explicitName?.trim();

	const ordered = [
		fromSource || undefined,
		explicit && explicit !== fromSource ? explicit : undefined,
		...LEGACY_CUSTOM_CODE_COMPONENT_NAMES,
	].filter((n): n is string => Boolean(n));

	return [...new Set(ordered)];
}

/** PascalCase `function Foo(` declarations in precompiled output (older saves without componentNames). */
export function extractFunctionNamesFromTransformed(
	transformedCode: string
): string[] {
	const legacyNames = new Set<string>(LEGACY_CUSTOM_CODE_COMPONENT_NAMES);
	const names: string[] = [];
	const re = /function\s+([A-Za-z_$][\w$]*)\s*\(/g;
	let match = re.exec(transformedCode);
	while (match) {
		const name = match[1];
		if (name && !legacyNames.has(name)) {
			names.push(name);
		}
		match = re.exec(transformedCode);
	}
	return [...new Set(names)];
}

export function mergeComponentLookupNames(
	primary: string[],
	transformedCode?: string
): string[] {
	const fromCode = transformedCode
		? extractFunctionNamesFromTransformed(transformedCode)
		: [];
	return [
		...new Set([
			...primary,
			...fromCode,
			...LEGACY_CUSTOM_CODE_COMPONENT_NAMES,
		]),
	];
}

export function buildRuntimeComponentLookupBody(names: string[]): string {
	const list = [...new Set(names.filter(Boolean))];
	if (list.length === 0) {
		return "return null;";
	}
	const checks = list
		.map((n) => `if (typeof ${n} !== 'undefined') return ${n};`)
		.join("\n      ");
	return `${checks}\n      return null;`;
}

export function buildIframeComponentResolverScript(names: string[]): string {
	const list = [...new Set(names.filter(Boolean))];
	if (list.length === 0) {
		return "const ComponentName = null;";
	}
	const chain = list
		.map((n) => `typeof ${n} !== 'undefined' ? ${n}`)
		.join(" :\n      ");
	return `const ComponentName = ${chain} : null;`;
}
